import { SignatureV4 } from '@aws-sdk/signature-v4';
import { Sha256 } from '@aws-crypto/sha256-js';
import { HttpRequest } from '@aws-sdk/protocol-http';
import { STS } from 'aws-sdk';
import {
    AMAZON_AWS,
    API_IAM_ROLE_MAPPING,
    PATH_PROD,
    SERVICE_EXECUTE_API
} from 'src/components/constants/constants';
import {
    getCachedCredentials,
    setCachedCredentials,
    getPendingStsPromise,
    setPendingStsPromise
} from './StsCredentialCache';
import { parse } from 'querystring';


export const getRegionFromUrl = (url: string): string => {
    return url.split(SERVICE_EXECUTE_API)[1].split(AMAZON_AWS)[0].split('.')[1];
};

export const getHostFromUrl = (url: string): string => {
    return url.split('/')[2];
};

export const getSignedRequest = async (
    urlToRequest: string,
    method: string,
    path: string,
    userContext: { authenticationToken: string; userAlias: any },
    requestBody?: any
) => {
    const isGetOrHead = method === 'GET' || method === 'HEAD';
    const body = isGetOrHead ? undefined : JSON.stringify(requestBody || '');

    const request = {
        host: getHostFromUrl(urlToRequest),
        url: urlToRequest + path,
        region: getRegionFromUrl(urlToRequest),
        service: SERVICE_EXECUTE_API,
        method,
        path: PATH_PROD + path,
        data: requestBody || '',
        body,
        headers: {
            'content-type': 'application/json'
        }
    };

    return await signRequestWithMidwayToken(
        urlToRequest,
        userContext.authenticationToken,
        request,
        userContext.userAlias
    );
};

export const signRequestWithMidwayToken = async (
    urlToRequest: string,
    midwayToken: string,
    request: any,
    userAlias: any
) => {
    const roleARN =
        API_IAM_ROLE_MAPPING[
            'https://3ewjdxae17.execute-api.us-west-2.amazonaws.com/prod' as keyof typeof API_IAM_ROLE_MAPPING
            ];

    const now = Date.now();
    const bufferMs = 60 * 1000; // 1-minute buffer

    let credentials;
    const cached = getCachedCredentials();
    const expiresAtTime = cached?.expiresAt?.getTime?.() ?? 0;

    console.log(
        `cached: ${!!cached?.credentials}, expiresAt - buffer: ${expiresAtTime - bufferMs}, now: ${now}`
    );

    if (cached && expiresAtTime - bufferMs > now) {
        console.log('Using cached STS credentials');
        credentials = cached.credentials;
    } else {
        const existingPromise = getPendingStsPromise();
        if (existingPromise) {
            console.log('Waiting for in-flight STS credentials');
            credentials = await existingPromise;
        } else {
            console.log('Fetching new STS credentials');
            const sts = new STS({ region: request.region });

            const stsPromise = sts
                .assumeRoleWithWebIdentity({
                    RoleArn: roleARN,
                    RoleSessionName: `TestPilotAPIAccess-${userAlias}`,
                    WebIdentityToken: midwayToken
                })
                .promise()
                .then((response) => {
                    if (!response.Credentials) throw new Error('STS returned no credentials');
                    setCachedCredentials(response.Credentials);
                    console.log('🗃️  Cached STS credentials until:', response.Credentials.Expiration);
                    return response.Credentials;
                })
                .finally(() => {
                    setPendingStsPromise(null);
                });

            setPendingStsPromise(stsPromise);
            credentials = await stsPromise;
        }
    }

    const parsedUrl = new URL(request.url);
    const query: Record<string, string> = {};
    parsedUrl.searchParams.forEach((value, key) => {
        query[key] = value;
    });
    const httpRequest = new HttpRequest({
        method: request.method,
        hostname: parsedUrl.hostname,
        path: parsedUrl.pathname,
        query,
        headers: {
            'content-type': 'application/json',
            host: parsedUrl.hostname
        },
        body: request.method === 'GET' || request.method === 'HEAD' ? undefined : request.body
    });

    const signer = new SignatureV4({
        credentials: {
            accessKeyId: credentials.AccessKeyId!,
            secretAccessKey: credentials.SecretAccessKey!,
            sessionToken: credentials.SessionToken!
        },
        region: request.region,
        service: 'execute-api',
        sha256: Sha256
    });

    const signedRequest = await signer.sign(httpRequest);

    return {
        url: request.url,
        method: request.method,
        headers: {
            ...signedRequest.headers
        },
        body:
            request.method === 'GET' || request.method === 'HEAD'
                ? undefined
                : request.body
    };
};
